import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';

/**
 * Asks before a design is destroyed, and says who it takes with it.
 *
 * Deleting one is not a local act: a branch pointing at it falls back to the
 * standard receipt, so the next customer at that counter gets a different bill.
 * Naming the branches is the whole reason this box exists — a bare "are you
 * sure?" would tell a shop nothing it did not already know.
 */
export const ConfirmDeleteDesign = ({ name, usedBy = [], busy, onCancel, onConfirm }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const names = usedBy.map((b) => b.name).join(', ');

  return (
    <div
      className="fixed inset-0 z-[950] bg-black/50 flex items-center justify-center p-4"
      onClick={onCancel}
      role="presentation"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.14 }}
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-label={`Delete ${name}`}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
      >
        <div className="px-6 pt-6 pb-5 text-center">
          <div
            className="w-12 h-12 mx-auto rounded-full grid place-items-center text-rose-500 mb-3"
            style={{ background: 'rgba(244,63,94,0.10)' }}
            aria-hidden="true"
          >
            <AlertTriangle size={20} />
          </div>
          <h4 className="text-base font-extrabold text-[#0d0d0d]">
            Delete “{name || 'this design'}”?
          </h4>

          {/* Only shown when it is true. A warning about branches on a design
              no branch uses is noise that teaches people to skip the box. */}
          {names ? (
            <p className="mt-2 text-[12px] font-medium text-slate-500 leading-relaxed">
              <span className="font-bold text-rose-500">{names}</span>{' '}
              {usedBy.length === 1 ? 'prints' : 'print'} with this design.{' '}
              {usedBy.length === 1 ? 'It' : 'They'} will fall back to the standard receipt.
            </p>
          ) : (
            <p className="mt-2 text-[12px] font-medium text-slate-500 leading-relaxed">
              No branch is using it. Nothing else changes.
            </p>
          )}
        </div>

        <div className="flex border-t border-slate-100">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </motion.div>
    </div>
  );
};
ConfirmDeleteDesign.propTypes = {
  name: PropTypes.string,
  usedBy: PropTypes.array,
  busy: PropTypes.bool,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
};
